// The signed-in app: shell with the organization's navigation, home, members, settings and the account page.
import { createContext, useContext, useEffect, useRef, useState, type ComponentProps, type FormEvent, type ReactNode } from "react";
import { Link, NavLink, Navigate, Outlet, useMatch, useNavigate, useParams } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { APP_NAME, ApiError, api, type Me, type Role } from "../../lib/api";
import { useMe, useSetMe } from "../../lib/session";
import { Alert, Avatar, Badge, Button, Card, Dialog, Field, Logo, Select, Spinner, cx } from "../../components/ui";
import { Icon } from "../../components/icons";
import { LiveProvider } from "../../lib/live";
import { keys, type ConversationSummary } from "../../lib/chat";
import { NotificationsBell } from "../chat/notifications";
import { IncomingCalls } from "../calls/incoming";
import { DesktopNotifications, NotificationsPrompt } from "../chat/desktop";
import { MessagePopups } from "../chat/popups";
import { FullPageSpinner, ProviderLogo, useSignup } from "../auth";
import { PeopleProvider, activeStatus, useNow, usePeople, type Person } from "../../lib/people";
import { DndChip, PersonButton, ProfileCardProvider, ProfileEditor, TwoFactorCard, YourStatus } from "../profile";
import { PresenceDot, usePresence } from "../chat/dialogs";
import { DomainsCard, JoinRequests, MeetingRulesCard, POLICY_CODES, PolicyBlock, SecurityCard } from "./security";
import { JoinableOrgs } from "./joinable";
import { BulkInviteDialog, RemoveMemberDialog } from "./members-tools";
import { AnnouncementBanner } from "../../components/announcements";

export type OrgSettingsData = {
  allowMembersToInvite?: boolean;
  signInMethods?: ("password" | "email_link" | "google" | "microsoft")[] | null;
  requireMfa?: boolean;
  sessionMaxHours?: number | null;
  recordingAllowed?: "everyone" | "admins" | "nobody";
  recordingRetentionDays?: number | null;
  guestsAllowed?: boolean;
  lobbyForEveryone?: boolean;
  maxMeetingMinutes?: number | null;
  clientsEnabled?: boolean;
};

// Whether this person may start a recording under the organization's rule.
export const mayRecord = (org: Org, role: Role) => {
  const rule = org.settings.recordingAllowed ?? "everyone";
  return rule === "everyone" || (rule === "admins" && atLeast(role, "admin"));
};
export type Org = { id: string; name: string; slug: string; settings: OrgSettingsData; createdAt: string };
export type OrgCtx = { org: Org; role: Role; me: Me };
type Member = { id: string; name: string; email: string; role: Role; status: "active" | "suspended"; joinedAt: string };
type Invitation = { id: string; email: string; role: Role; expiresAt: string; invitedBy: string | null };

const RANK: Record<Role, number> = { guest: 0, member: 1, admin: 2, owner: 3 };
export const atLeast = (role: Role, min: Role) => RANK[role] >= RANK[min];
export const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "Something went wrong. Please try again.");

export function OrgShell() {
  const { slug = "" } = useParams();
  const me = useMe();
  const org = useQuery({ queryKey: ["org", slug], queryFn: () => api<{ organization: Org; role: Role }>(`/orgs/${slug}`), enabled: Boolean(me.data), retry: false });
  if (me.isLoading) return <FullPageSpinner />;
  if (!me.data) return <Navigate to={`/login?next=${encodeURIComponent(`/o/${slug}`)}`} replace />;
  if (org.isLoading) return <FullPageSpinner />;
  if (org.error instanceof ApiError && org.error.code === "ORG_SUSPENDED") return <Suspended me={me.data} slug={slug} message={org.error.message} />;
  if (org.error instanceof ApiError && org.error.code === "ORG_IP_BLOCKED") {
    return <Suspended me={me.data} slug={slug} title="Not available from this network" message={org.error.message} codes={Boolean(org.error.details.codes)} onUnlocked={() => void org.refetch()} />;
  }
  if (org.error instanceof ApiError && POLICY_CODES.includes(org.error.code)) return <PolicyBlock slug={slug} error={org.error} onRetry={() => void org.refetch()} />;
  if (org.error || !org.data) return <Navigate to="/" replace />;
  const ctx: OrgCtx = { org: org.data.organization, role: org.data.role, me: me.data };
  return <LiveProvider slug={slug} me={me.data.user.id}><Shell ctx={ctx} /></LiveProvider>;
}

// Suspended, or not open to this network: the way in (an access code, when the organization has one) or out (other
// organizations, the admin panel, signing out).
function Suspended({ me, slug, message, title = "Organization suspended", codes = false, onUnlocked }: { me: Me; slug: string; message: string; title?: string; codes?: boolean; onUnlocked?: () => void }) {
  const others = me.organizations.filter((o) => o.slug !== slug && !o.suspended);
  const qc = useQueryClient();
  const navigate = useNavigate();
  async function signOut() {
    await api("/auth/logout", { method: "POST" }).catch(() => undefined);
    qc.clear();
    navigate("/login", { replace: true });
  }
  return (
    <div className="grid min-h-dvh place-items-center p-6">
      <Card className="w-full max-w-md text-center">
        <div className="mb-4 flex justify-center"><Logo withName={false} /></div>
        <h1 className="text-lg font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{message}</p>
        {codes && onUnlocked && <AccessCodeForm slug={slug} onUnlocked={onUnlocked} />}
        {others.length > 0 && (
          <div className="mt-6 space-y-2 text-left">
            <p className="text-sm font-medium">Your other organizations</p>
            {others.map((o) => <Link key={o.id} to={`/o/${o.slug}`} className="block rounded-lg px-3 py-2 text-sm ring-1 ring-slate-200 hover:bg-slate-50 dark:ring-slate-700 dark:hover:bg-slate-800">{o.name}</Link>)}
          </div>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {me.user.platformRole && <Link to="/admin"><Button variant="secondary">Open the admin panel</Button></Link>}
          <Button variant="ghost" onClick={signOut}>Sign out</Button>
        </div>
      </Card>
    </div>
  );
}

// An access code given to this organization's testers: this browser can then use it from any network until it ends.
function AccessCodeForm({ slug, onUnlocked }: { slug: string; onUnlocked: () => void }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(undefined);
    try {
      await api(`/orgs/${slug}/access-code`, { method: "POST", body: { code } });
      onUnlocked();
    } catch (err) { setError(errorText(err)); setBusy(false); }
  }
  return (
    <form onSubmit={submit} className="mt-6 space-y-3 text-left">
      <Field label="Access code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="ABCD-EFGH-JKLM" autoComplete="off" autoCapitalize="characters" spellCheck={false} maxLength={40} required error={error} />
      <Button type="submit" className="w-full" loading={busy} disabled={!code.trim()}>Continue</Button>
    </form>
  );
}

// The organization, signed-in person and role, for every screen inside the shell (pages, panels, the call screen).
const OrgContext = createContext<OrgCtx | null>(null);
export function useOrg(): OrgCtx {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error("useOrg outside the organization shell");
  return ctx;
}
export const useOrgShell = useOrg;

function Shell({ ctx }: { ctx: OrgCtx }) {
  return (
    <OrgContext.Provider value={ctx}>
      <PeopleProvider slug={ctx.org.slug}><ProfileCardProvider><ShellLayout ctx={ctx} /></ProfileCardProvider></PeopleProvider>
    </OrgContext.Provider>
  );
}

function ShellLayout({ ctx }: { ctx: OrgCtx }) {
  const slug = ctx.org.slug;
  const fullBleed = Boolean(useMatch("/o/:slug/chat/*"));
  const inCall = Boolean(useMatch("/o/:slug/call/:callId"));
  // Unread messages (muted conversations don't count) for the Chat badge and the tab title.
  const conversations = useQuery({ queryKey: keys.conversations(slug), queryFn: () => api<{ conversations: ConversationSummary[] }>(`/orgs/${slug}/conversations`) });
  const unread = (conversations.data?.conversations ?? []).reduce((n, c) => n + (c.muted ? 0 : c.unread), 0);
  useEffect(() => {
    document.title = unread ? `(${unread}) ${APP_NAME}` : APP_NAME;
    return () => { document.title = APP_NAME; };
  }, [unread]);

  // A call takes the whole window (incoming calls can still ring on top).
  if (inCall) return <><Outlet context={ctx} /><IncomingCalls /><DesktopNotifications slug={slug} /><MessagePopups slug={slug} /></>;

  const nav = [
    { to: "", icon: "home" as const, label: "Home", end: true },
    { to: "chat", icon: "chat" as const, label: "Chat" },
    { to: "calls", icon: "phone" as const, label: "Calls" },
    { to: "meetings", icon: "video" as const, label: "Meetings" },
    // Guests see projects only as clients, and only where the organization works with clients.
    ...(atLeast(ctx.role, "member") || ctx.org.settings.clientsEnabled ? [{ to: "projects", icon: "board" as const, label: "Projects" }] : []),
    ...(atLeast(ctx.role, "member") ? [{ to: "members", icon: "users" as const, label: "People" }] : []),
    ...(atLeast(ctx.role, "admin") ? [{ to: "admin", icon: "shield" as const, label: "Admin" }, { to: "reports", icon: "chart" as const, label: "Reports" }] : []),
    { to: "settings", icon: "settings" as const, label: "Settings" },
  ];

  return (
    <div className="flex h-dvh">
      <nav className="sticky top-0 hidden h-dvh w-20 shrink-0 flex-col items-center gap-1 border-r border-slate-200 bg-white py-3 md:flex dark:border-slate-800 dark:bg-slate-900" aria-label="Main">
        <Link to={`/o/${slug}`} className="mb-3" aria-label={`${APP_NAME} home`}><Logo withName={false} /></Link>
        {nav.map((item) => (
          <NavLink
            key={item.to}
            to={item.to ? `/o/${slug}/${item.to}` : `/o/${slug}`}
            end={item.end}
            className={({ isActive }) => cx(
              "flex w-16 flex-col items-center gap-1 rounded-xl py-2 text-[11px] font-medium transition-colors",
              isActive ? "bg-brand-50 text-brand-700 dark:bg-brand-700/25 dark:text-brand-100" : "text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800",
            )}
          >
            <span className="relative">
              <Icon name={item.icon} />
              {item.to === "chat" && unread > 0 && <span className="absolute -right-2.5 -top-1.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-4 text-white">{unread > 99 ? "99+" : unread}</span>}
            </span>
            {item.label}
          </NavLink>
        ))}
        <div className="mt-auto"><AccountMenu me={ctx.me} slug={slug} /></div>
      </nav>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <AnnouncementBanner />
        <header className="flex h-14 shrink-0 items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 md:px-6 dark:border-slate-800 dark:bg-slate-900">
          <OrgSwitcher me={ctx.me} current={ctx.org} />
          <div className="flex shrink-0 items-center gap-2 md:gap-3">
            <DndChip /><NotificationsBell slug={slug} />
            <Badge tone="brand">{ctx.role}</Badge>
            <div className="md:hidden"><AccountMenu me={ctx.me} slug={slug} /></div>
          </div>
        </header>
        <NotificationsPrompt />
        <main className={cx("min-h-0 flex-1", fullBleed ? "overflow-hidden" : "overflow-y-auto p-4 md:p-6")}><Outlet context={ctx} /></main>
        <MobileTabs nav={nav} slug={slug} unread={unread} />
      </div>
      <IncomingCalls />
      <DesktopNotifications slug={slug} />
      <MessagePopups slug={slug} />
    </div>
  );
}

type NavItem = { to: string; icon: ComponentProps<typeof Icon>["name"]; label: string; end?: boolean };

// Where there is no room for the rail, the same places become a bottom bar: the four people use most, then More for
// the rest. A row of the layout, not something floating over it, so it never covers a composer or a list's last line.
function MobileTabs({ nav, slug, unread }: { nav: NavItem[]; slug: string; unread: number }) {
  const [more, setMore] = useState(false);
  const shown = nav.slice(0, 4);
  const rest = nav.slice(4);
  const href = (item: NavItem) => (item.to ? `/o/${slug}/${item.to}` : `/o/${slug}`);
  const tab = (active: boolean) => cx(
    "flex min-w-0 flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium",
    active ? "text-brand-700 dark:text-brand-100" : "text-slate-600 dark:text-slate-400",
  );
  return (
    <>
      {more && (
        <div className="fixed inset-0 z-40 md:hidden">
          <button type="button" aria-label="Close" className="absolute inset-0 bg-slate-900/40" onClick={() => setMore(false)} />
          <div role="menu" className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white p-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] shadow-lg dark:bg-slate-900">
            {rest.map((item) => (
              <NavLink
                role="menuitem" key={item.to} to={href(item)} end={item.end} onClick={() => setMore(false)}
                className={({ isActive }) => cx(
                  "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium",
                  isActive ? "bg-brand-50 text-brand-700 dark:bg-brand-700/25 dark:text-brand-100" : "text-slate-700 dark:text-slate-300",
                )}
              >
                <Icon name={item.icon} />{item.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
      <nav className="flex shrink-0 items-stretch border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden dark:border-slate-800 dark:bg-slate-900" aria-label="Main">
        {shown.map((item) => (
          <NavLink key={item.to} to={href(item)} end={item.end} className={({ isActive }) => tab(isActive)}>
            <span className="relative">
              <Icon name={item.icon} />
              {item.to === "chat" && unread > 0 && <span className="absolute -right-2.5 -top-1.5 min-w-4 rounded-full bg-red-600 px-1 text-center text-[10px] font-semibold leading-4 text-white">{unread > 99 ? "99+" : unread}</span>}
            </span>
            <span className="truncate">{item.label}</span>
          </NavLink>
        ))}
        {rest.length > 0 && (
          <button type="button" onClick={() => setMore((o) => !o)} aria-expanded={more} className={tab(more)}>
            <Icon name="more" /><span className="truncate">More</span>
          </button>
        )}
      </nav>
    </>
  );
}

function OrgSwitcher({ me, current }: { me: Me; current: Org }) {
  const [open, setOpen] = useState(false);
  const signup = useSignup();
  return (
    <div className="relative min-w-0">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 font-semibold hover:bg-slate-100 dark:hover:bg-slate-800" aria-expanded={open} aria-haspopup="menu">
        <span className="truncate">{current.name}</span><Icon name="chevron" className="h-4 w-4 shrink-0" />
      </button>
      {open && (
        <div role="menu" className="absolute left-0 z-20 mt-1 w-64 rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700" onMouseLeave={() => setOpen(false)}>
          {me.organizations.map((o) => (
            <Link role="menuitem" key={o.id} to={`/o/${o.slug}`} onClick={() => setOpen(false)} className={cx("flex items-center justify-between rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800", o.slug === current.slug && "font-semibold")}>
              {o.name}<span className="text-xs text-slate-500">{o.suspended ? "suspended" : o.role}</span>
            </Link>
          ))}
          <JoinableOrgs compact />
          {(signup.open || me.user.platformRole) && <Link role="menuitem" to="/welcome" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-brand-600 hover:bg-slate-100 dark:hover:bg-slate-800">
            <Icon name="plus" className="h-4 w-4" />New organization
          </Link>}
        </div>
      )}
    </div>
  );
}

function AccountMenu({ me, slug }: { me: Me; slug: string }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const box = useRef<HTMLDivElement>(null);
  // Closes on a click outside (the status dialog opened from it counts as inside) or Escape.
  useEffect(() => {
    if (!open) return;
    const down = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    const key = (e: KeyboardEvent) => { if (e.key === "Escape" && !document.querySelector("dialog[open]")) setOpen(false); };
    document.addEventListener("mousedown", down);
    document.addEventListener("keydown", key);
    return () => { document.removeEventListener("mousedown", down); document.removeEventListener("keydown", key); };
  }, [open]);
  async function signOut() {
    await api("/auth/logout", { method: "POST" }).catch(() => undefined);
    qc.clear();
    navigate("/login", { replace: true });
  }
  return (
    <div className="relative" ref={box}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-label="Your account" aria-expanded={open} aria-haspopup="menu" className="relative rounded-full">
        <Avatar name={me.user.name} src={me.user.avatarUrl} />
        <PresenceDot status="online" userId={me.user.id} className="absolute -bottom-0.5 -right-0.5" />
      </button>
      {open && (
        <div role="menu" className="fixed right-3 top-16 z-40 w-72 max-w-[calc(100vw-1.5rem)] rounded-xl bg-white p-1 shadow-lg ring-1 ring-slate-200 md:absolute md:bottom-0 md:left-12 md:right-auto md:top-auto md:z-20 dark:bg-slate-900 dark:ring-slate-700">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar name={me.user.name} src={me.user.avatarUrl} />
            <div className="min-w-0 flex-1"><div className="truncate font-medium">{me.user.name}</div><div className="truncate text-xs text-slate-500">{me.user.jobTitle || me.user.email}</div></div>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close menu" className="rounded-md p-2 [@media(pointer:coarse)]:p-3 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"><Icon name="x" className="h-4 w-4" /></button>
          </div>
          <YourStatus />
          <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
          <Link role="menuitem" to={`/o/${slug}/account`} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800">Your profile and account</Link>
          {me.user.platformRole && <Link role="menuitem" to="/admin" onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800">Platform admin</Link>}
          <button role="menuitem" type="button" onClick={signOut} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-red-600 hover:bg-slate-100 dark:hover:bg-slate-800">
            <Icon name="logout" className="h-4 w-4" />Sign out
          </button>
        </div>
      )}
    </div>
  );
}


function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div><h1 className="text-2xl font-semibold tracking-tight">{title}</h1>{subtitle && <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{subtitle}</p>}</div>
      {action}
    </div>
  );
}

export function OrgHome() {
  const { org, me, role } = useOrg();
  const cards = [
    { title: "Chat", text: "Direct messages, group chats, teams and channels.", icon: "chat" as const, soon: false, to: "chat" },
    { title: "Calls", text: "One-to-one and group video and audio calls.", icon: "phone" as const, soon: false, to: "calls" },
    { title: "Meetings", text: "Meeting links, guests, waiting room, scheduling.", icon: "video" as const, soon: false, to: "meetings" },
  ];
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title={`Welcome, ${me.user.name.split(" ")[0]}`} subtitle={`${org.name} on ${APP_NAME}`} action={atLeast(role, "admin") ? <Link to={`/o/${org.slug}/members?invite=1`}><Button><Icon name="plus" className="h-4 w-4" />Invite people</Button></Link> : undefined} />
      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map((c) => {
          const card = (
            <Card className={c.to ? "h-full transition-shadow hover:shadow-md" : "h-full"}>
              <div className="mb-3 grid h-10 w-10 place-items-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-700/30"><Icon name={c.icon} /></div>
              <h2 className="flex items-center gap-2 font-semibold">{c.title}{c.soon && <Badge>Coming next</Badge>}</h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{c.text}</p>
            </Card>
          );
          return c.to ? <Link key={c.title} to={`/o/${org.slug}/${c.to}`}>{card}</Link> : <div key={c.title}>{card}</div>;
        })}
      </div>
    </div>
  );
}

export function ComingSoon({ title }: { title: string }) {
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title={title} />
      <Card><p className="text-sm text-slate-600 dark:text-slate-400">{title} is being built and arrives in the next phase.</p></Card>
    </div>
  );
}

export function Members() {
  const { org, role, me } = useOrg();
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(() => new URLSearchParams(window.location.search).get("invite") === "1");
  const [error, setError] = useState<string | null>(null);
  const members = useQuery({ queryKey: ["members", org.slug], queryFn: () => api<{ members: Member[] }>(`/orgs/${org.slug}/members`) });
  const people = usePeople();
  const presence = usePresence();
  const now = useNow();
  const statusLine = (p: Person | undefined) => { const s = activeStatus(p?.status, now); return s ? `${s.emoji ? `${s.emoji} ` : ""}${s.text}`.trim() : ""; };
  const invitations = useQuery({ queryKey: ["invitations", org.slug], queryFn: () => api<{ invitations: Invitation[] }>(`/orgs/${org.slug}/invitations`), enabled: atLeast(role, "admin") });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["members", org.slug] }); qc.invalidateQueries({ queryKey: ["invitations", org.slug] }); };

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Pick<Member, "role" | "status">> }) => api(`/orgs/${org.slug}/members/${id}`, { method: "PATCH", body: patch }),
    onSuccess: refresh,
    onError: (e) => setError(errorText(e)),
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [removing, setRemoving] = useState<Member | null>(null);
  const revoke = useMutation({ mutationFn: (id: string) => api(`/orgs/${org.slug}/invitations/${id}`, { method: "DELETE" }), onSuccess: refresh });

  const canManage = (m: Member) => m.id !== me.user.id && (role === "owner" || (role === "admin" && !atLeast(m.role, "admin")));
  const roleOptions: Role[] = role === "owner" ? ["owner", "admin", "member", "guest"] : ["member", "guest"];
  const canInvite = atLeast(role, "admin") || (role === "member" && org.settings.allowMembersToInvite);

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="People" subtitle={`Everyone in ${org.name}.`} action={canInvite ? (
        <div className="flex gap-2">
          {atLeast(role, "admin") && <Button variant="secondary" onClick={() => setBulkOpen(true)}>Invite many</Button>}
          <Button onClick={() => setInviteOpen(true)}><Icon name="plus" className="h-4 w-4" />Invite people</Button>
        </div>
      ) : undefined} />
      {error && <div className="mb-4"><Alert>{error}</Alert></div>}
      <Card className="p-0">
        {members.isLoading ? <div className="flex justify-center p-8 text-brand-600"><Spinner /></div> : (
          <ul className="divide-y divide-slate-100 dark:divide-slate-800">
            {members.data?.members.map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                <PersonButton userId={m.id} label={m.name} className="relative"><Avatar name={m.name} userId={m.id} /><PresenceDot status={presence[m.id]} userId={m.id} className="absolute -bottom-0.5 -right-0.5" /></PersonButton>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">
                    <PersonButton userId={m.id} label={m.name} className="rounded hover:underline">{m.name}</PersonButton>{m.id === me.user.id && <span className="text-slate-500"> (you)</span>}
                    {people.get(m.id)?.jobTitle && <span className="font-normal text-slate-500"> · {people.get(m.id)!.jobTitle}</span>}
                  </div>
                  <div className="truncate text-sm text-slate-500">{m.email}{statusLine(people.get(m.id)) && <span> · {statusLine(people.get(m.id))}</span>}</div>
                </div>
                {m.status === "suspended" && <Badge tone="red">Suspended</Badge>}
                {canManage(m) ? (
                  <div className="flex items-center gap-2">
                    <select aria-label={`Role for ${m.name}`} value={m.role} onChange={(e) => { setError(null); update.mutate({ id: m.id, patch: { role: e.target.value as Role } }); }} className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900">
                      {[...new Set([m.role, ...roleOptions])].map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <Button size="sm" variant="ghost" onClick={() => { setError(null); update.mutate({ id: m.id, patch: { status: m.status === "active" ? "suspended" : "active" } }); }}>{m.status === "active" ? "Suspend" : "Restore"}</Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => { setError(null); setRemoving(m); }}>Remove</Button>
                  </div>
                ) : <Badge tone={m.role === "owner" ? "brand" : "slate"}>{m.role}</Badge>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {atLeast(role, "admin") && (invitations.data?.invitations.length ?? 0) > 0 && (
        <>
          <h2 className="mb-3 mt-8 text-lg font-semibold">Waiting to join</h2>
          <Card className="p-0">
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {invitations.data!.invitations.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1"><div className="truncate font-medium">{i.email}</div><div className="text-sm text-slate-500">Invited as {i.role}{i.invitedBy ? ` by ${i.invitedBy}` : ""} · expires {new Date(i.expiresAt).toLocaleDateString()}</div></div>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => revoke.mutate(i.id)}>Revoke</Button>
                </li>
              ))}
            </ul>
          </Card>
        </>
      )}

      {atLeast(role, "admin") && <JoinRequests />}
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onDone={refresh} />
      {atLeast(role, "admin") && <BulkInviteDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={refresh} />}
      {removing && <RemoveMemberDialog member={removing} members={members.data?.members ?? []} onClose={() => setRemoving(null)} onDone={refresh} />}
    </div>
  );
}

function InviteDialog({ open, onClose, onDone }: { open: boolean; onClose: () => void; onDone: () => void }) {
  const { org, role } = useOrg();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ sent: string[]; skipped: { email: string; reason: string }[] } | null>(null);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const emails = String(data.get("emails") ?? "").split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api<{ sent: string[]; skipped: { email: string; reason: string }[] }>(`/orgs/${org.slug}/invitations`, { method: "POST", body: { emails, role: data.get("role") } });
      setResult(r);
      onDone();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onClose={() => { setResult(null); setError(null); onClose(); }} title={`Invite people to ${org.name}`}>
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}
        {result && (
          <Alert kind="success">
            {result.sent.length ? `Invitation sent to ${result.sent.join(", ")}.` : "No invitations sent."}
            {result.skipped.map((s) => <div key={s.email}>{s.email}: {s.reason}</div>)}
          </Alert>
        )}
        <div>
          <label htmlFor="invite-emails" className="mb-1.5 block text-sm font-medium">Email addresses</label>
          <textarea id="invite-emails" name="emails" rows={3} required placeholder="name@company.com, another@company.com" className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900" />
          <p className="mt-1.5 text-sm text-slate-500">Up to 20 at a time, separated by commas or new lines.</p>
        </div>
        <Select label="Join as" name="role" defaultValue="member">
          <option value="member">Member</option>
          <option value="guest">Guest (limited access)</option>
          {role === "owner" && <option value="admin">Admin</option>}
        </Select>
        <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={onClose}>Close</Button><Button type="submit" loading={busy}>Send invitations</Button></div>
      </form>
    </Dialog>
  );
}

export function OrgSettings() {
  const { org, role } = useOrg();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const setMe = useSetMe();
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isAdmin = atLeast(role, "admin");
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    setSaved(false); setError(null);
    try {
      await api(`/orgs/${org.slug}`, { method: "PATCH", body: { name: String(data.get("name")), settings: { allowMembersToInvite: data.get("allowMembersToInvite") === "on", clientsEnabled: data.get("clientsEnabled") === "on" } } });
      await qc.invalidateQueries({ queryKey: ["org", org.slug] });
      setMe(await api<Me>("/auth/me"));
      setSaved(true);
    } catch (err) { setError(errorText(err)); }
  }
  async function leave() {
    if (!confirm(`Leave ${org.name}?`)) return;
    try {
      await api(`/orgs/${org.slug}/members/me`, { method: "DELETE" });
      const me = await api<Me>("/auth/me");
      setMe(me);
      navigate(me.organizations[0] ? `/o/${me.organizations[0].slug}` : "/welcome", { replace: true });
    } catch (err) { setError(errorText(err)); }
  }
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="Organization settings" />
      {error && <Alert>{error}</Alert>}
      {saved && <Alert kind="success">Saved.</Alert>}
      <Card>
        <form onSubmit={save} className="space-y-4">
          <Field label="Name" name="name" defaultValue={org.name} disabled={!isAdmin} required />
          <Field label="Address" value={`/o/${org.slug}`} readOnly disabled />
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" name="allowMembersToInvite" defaultChecked={Boolean(org.settings.allowMembersToInvite)} disabled={!isAdmin} className="mt-0.5 h-4 w-4 rounded" />
            <span><span className="font-medium">Members can invite people</span><br /><span className="text-slate-500">Otherwise only admins and owners can.</span></span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input type="checkbox" name="clientsEnabled" defaultChecked={Boolean(org.settings.clientsEnabled)} disabled={!isAdmin} className="mt-0.5 h-4 w-4 rounded" />
            <span><span className="font-medium">We work with clients</span><br /><span className="text-slate-500">Invite a client to a project: they see that project only, can ask for things and approve work, and never see what is marked team only. Turning this off takes clients' access away; nothing is deleted.</span></span>
          </label>
          {isAdmin && <div className="flex justify-end"><Button type="submit">Save</Button></div>}
        </form>
      </Card>
      {isAdmin && <SecurityCard />}
      {isAdmin && <DomainsCard />}
      {isAdmin && <MeetingRulesCard />}
      <Card>
        <h2 className="font-semibold">Leave {org.name}</h2>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">You'll lose access to its chats, calls and meetings. An owner has to make someone else an owner first.</p>
        <Button variant="danger" className="mt-4" onClick={leave}>Leave organization</Button>
      </Card>
    </div>
  );
}

export function Account() {
  const { me } = useOrg();
  const setMe = useSetMe();
  const [msg, setMsg] = useState<{ kind: "success" | "error"; text: string } | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  async function changePassword(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    setMsg(null); setFields({});
    try {
      setMe(await api<Me>("/auth/password/change", { method: "POST", body: { currentPassword: data.get("currentPassword") || undefined, newPassword: data.get("newPassword") } }));
      form.reset();
      setMsg({ kind: "success", text: "Password changed. You've been signed out on your other devices." });
    } catch (err) {
      if (err instanceof ApiError) setFields(err.fields);
      setMsg({ kind: "error", text: errorText(err) });
    }
  }
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader title="Your account" subtitle={me.user.email} />
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      <ProfileEditor />
      <TwoFactorCard />
      <ConnectedAccounts />
      <Card>
        <h2 className="mb-4 font-semibold">{me.user.hasPassword ? "Change password" : "Set a password"}</h2>
        <form onSubmit={changePassword} className="space-y-4">
          {me.user.hasPassword && <Field label="Current password" name="currentPassword" type="password" autoComplete="current-password" required error={fields.currentPassword} />}
          <Field label="New password" name="newPassword" type="password" autoComplete="new-password" required error={fields.newPassword} hint="At least 10 characters." />
          <div className="flex justify-end"><Button type="submit">Save password</Button></div>
        </form>
      </Card>
    </div>
  );
}

// Google / Microsoft accounts that can sign in to this one.
function ConnectedAccounts() {
  const { org } = useOrg();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(() => new URLSearchParams(window.location.search).get("error"));
  const data = useQuery({ queryKey: ["identities"], queryFn: () => api<{ identities: { provider: "google" | "microsoft"; email: string | null }[]; available: { id: "google" | "microsoft"; label: string }[] }>("/auth/identities") });
  if (!data.data?.available.length) return null;
  const here = `/o/${org.slug}/account`;
  async function disconnect(provider: string) {
    setError(null);
    try { await api(`/auth/identities/${provider}`, { method: "DELETE" }); await qc.invalidateQueries({ queryKey: ["identities"] }); } catch (err) { setError(errorText(err)); }
  }
  return (
    <Card>
      <h2 className="font-semibold">Connected accounts</h2>
      <p className="mb-4 mt-1 text-sm text-slate-600 dark:text-slate-400">Sign in with these as well as your email.</p>
      {error && <div className="mb-3"><Alert>{error === "linked_elsewhere" ? "That account is already connected to someone else here." : error.length < 40 ? "Connecting didn't work. Please try again." : error}</Alert></div>}
      <ul className="space-y-2">
        {data.data.available.map((p) => {
          const linked = data.data!.identities.find((i) => i.provider === p.id);
          return (
            <li key={p.id} className="flex items-center gap-3 rounded-lg px-3 py-2 ring-1 ring-slate-200 dark:ring-slate-700">
              <ProviderLogo id={p.id} />
              <span className="min-w-0 flex-1 text-sm"><span className="font-medium">{p.label}</span>{linked?.email && <span className="text-slate-500"> · {linked.email}</span>}</span>
              {linked
                ? <Button size="sm" variant="ghost" onClick={() => disconnect(p.id)}>Disconnect</Button>
                : <a href={`/api/v1/auth/oauth/${p.id}/start?link=1&next=${encodeURIComponent(here)}`}><Button size="sm" variant="secondary">Connect</Button></a>}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
